import axios from "axios";
export const BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "http://localhost:4000";
export const API_BASE = `${BASE_URL}/api/v1`;
export const CHAT_SERVER_URL =
  process.env.REACT_APP_CHAT_SERVER_URL || "http://localhost:5000";


const AUTH_EXEMPT_ENDPOINTS = [
  "/admin/login",
  "/admin/register",
  "/admin/verify-email",
  "/admin/login-verify",
  "/admin/forgot-password",
  "/admin/reset-password",
  "/admin/google",
];

const getStoredToken = () => {
  if (typeof window === "undefined") return null;

  try {
    const storedUser = localStorage.getItem("adminUser");
    if (!storedUser) return null;
    const parsedUser = JSON.parse(storedUser);
    return parsedUser?.token || null;
  } catch (error) {
    localStorage.removeItem("adminUser");
    return null;
  }
};

const isAuthExemptRequest = (url = "") =>
  AUTH_EXEMPT_ENDPOINTS.some((endpoint) => url.includes(endpoint));

if (typeof window !== "undefined" && !window.__ADMIN_API_INTERCEPTORS__) {
  axios.interceptors.request.use(
    (config) => {
      const token = getStoredToken();
      if (token) {
        config.headers = config.headers || {};
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    (error) => Promise.reject(error),
  );

  axios.interceptors.response.use(
    (response) => response,
    (error) => {
      if (
        error?.response?.status === 401 &&
        !isAuthExemptRequest(error?.config?.url || "")
      ) {
        localStorage.removeItem("adminUser");
        const currentPath = window.location.pathname;
        if (currentPath !== "/login" && currentPath !== "/register") {
          window.location.assign("/login");
        }
      }
      return Promise.reject(error);
    },
  );

  window.__ADMIN_API_INTERCEPTORS__ = true;
}

const staticCache = {
  data: {},
  promises: {},
  TTL: 10000, // 10 seconds cache for static data
};

const cachedGet = (key, fetcher) => {
  const now = Date.now();
  if (staticCache.data[key] && now - staticCache.lastFetched[key] < staticCache.TTL) {
    return Promise.resolve(staticCache.data[key]);
  }
  if (staticCache.promises[key]) {
    return staticCache.promises[key];
  }

  staticCache.promises[key] = fetcher()
    .then((res) => {
      staticCache.data[key] = res;
      staticCache.lastFetched[key] = now;
      delete staticCache.promises[key];
      return res;
    })
    .catch((err) => {
      delete staticCache.promises[key];
      if (staticCache.data[key]) return Promise.resolve(staticCache.data[key]); // Fallback to stale data
      throw err;
    });

  return staticCache.promises[key];
};

staticCache.lastFetched = {};

export const api = {
  // Teachers
  getTeachers: () => axios.get(`${API_BASE}/teachers`),
  getAdmins: () => axios.get(`${API_BASE}/admins`),
  addTeacher: (data) => axios.post(`${API_BASE}/teachers`, data),
  updateTeacher: (teacherId, data) =>
    axios.put(`${API_BASE}/teachers/${teacherId}`, data),
  deleteTeacher: (teacherId) =>
    axios.delete(`${API_BASE}/teachers/${teacherId}`),

  // Subjects
  getSubjects: () => cachedGet("subjects", () => axios.get(`${API_BASE}/subjects`)),
  addSubject: (data) => axios.post(`${API_BASE}/subjects`, data),
  updateSubject: (subjectId, data) =>
    axios.put(`${API_BASE}/subjects/${subjectId}`, data),
  deleteSubject: (subjectId) =>
    axios.delete(`${API_BASE}/subjects/${subjectId}`),

  // Sections
  getSections: () => cachedGet("sections", () => axios.get(`${API_BASE}/sections`)),
  addSection: (data) => axios.post(`${API_BASE}/sections`, data),
  updateSection: (sectionId, data) =>
    axios.put(`${API_BASE}/sections/${sectionId}`, data),
  deleteSection: (sectionId) =>
    axios.delete(`${API_BASE}/sections/${sectionId}`),

  // Constraints
  getConstraints: () => axios.get(`${API_BASE}/constraints`),
  addConstraint: (data) => axios.post(`${API_BASE}/constraints`, data),
  deleteConstraint: (id) => axios.delete(`${API_BASE}/constraints/${id}`),

  // Timetable
  getTimetable: (sectionId) =>
    axios.get(`${API_BASE}/timetable-entries/${sectionId}`),
  getAllTimetables: () => axios.get(`${API_BASE}/timetable`),
  generateTimetable: (sectionId, preview = false) =>
    axios.post(`${API_BASE}/timetable/generate`, { sectionId, preview }),
  generateTimetableFromData: (data) =>
    axios.post(`${API_BASE}/timetable/generate-from-data`, data),
  generateTimetablePreview: (data) =>
    axios.post(`${API_BASE}/timetable/generate-preview`, data),
  importBulkData: (data) => axios.post(`${API_BASE}/data/import`, data),

  // Students
  getStudents: (params = {}) => axios.get(`${API_BASE}/students`, { params }),
  addStudent: (data) => axios.post(`${API_BASE}/students`, data),
  updateStudent: (studentId, data) =>
    axios.put(`${API_BASE}/students/${studentId}`, data),
  deleteStudent: (studentId) =>
    axios.delete(`${API_BASE}/students/${studentId}`),

  // Admin Timetable
  generateInstitutional: () =>
    axios.post(`${API_BASE}/timetable/generate-institutional`),
  startInstitutionalGenerationJob: () =>
    axios.post(`${API_BASE}/timetable/generate-institutional-job`),
  getInstitutionalGenerationJob: (jobId) =>
    axios.get(`${API_BASE}/timetable/generation-jobs/${jobId}`),
  regenerateSection: (sectionId, batchId) =>
    axios.post(`${API_BASE}/timetable/generate-section/${sectionId}`, {
      batchId,
    }),
  listBatches: () => axios.get(`${API_BASE}/timetable/batches`),
  getBatch: (batchId) => axios.get(`${API_BASE}/timetable/batch/${batchId}`),
  approveBatch: (batchId) =>
    axios.post(`${API_BASE}/timetable/batch/${batchId}/approve`),
  editEntry: (batchId, data) =>
    axios.put(`${API_BASE}/timetable/batch/${batchId}/entry`, data),
  lockSection: (batchId, sectionId, locked) =>
    axios.post(`${API_BASE}/timetable/batch/${batchId}/lock/${sectionId}`, {
      locked,
    }),
  validateBatch: (batchId) =>
    axios.get(`${API_BASE}/timetable/validate/${batchId}`),
  getTeacherBatchSchedule: (batchId, teacherId) =>
    axios.get(`${API_BASE}/timetable/teacher-schedule/${batchId}/${teacherId}`),
  getBatchSummary: (batchId) =>
    axios.get(`${API_BASE}/timetable/summary/${batchId}`),

  // QR & Attendance
  generateQR: (data) => axios.post(`${API_BASE}/attendance/generate-qr`, data),
  getAttendance: (params = {}) =>
    axios.get(`${API_BASE}/attendance`, { params }),
  markManualAttendance: (data) =>
    axios.post(`${API_BASE}/attendance/mark-manual`, data),
  endClassSession: (data) => axios.post(`${API_BASE}/class/session/end`, data),
  startClassSession: (data) =>
    axios.post(`${API_BASE}/class/session/start`, data),
  getClassSession: (classId) =>
    axios.get(`${API_BASE}/class/session/${classId}`),

  getTeacherSchedule: (teacherId) =>
    axios.get(`${API_BASE}/timetable/teacher/${teacherId}`),
  getTeacherTodaySchedule: (teacherId) =>
    axios.get(`${API_BASE}/timetable/teacher-today/${teacherId}`),
  getTeacherCurrentClass: (teacherId) =>
    axios.get(`${API_BASE}/timetable/teacher-current/${teacherId}`),
  getMySchedule: (teacherId) =>
    axios.get(`${API_BASE}/timetable/teacher/${teacherId}`),

  // Constraints (edit)
  updateConstraint: (id, data) =>
    axios.put(`${API_BASE}/constraints/${id}`, data),

  // Notifications
  getNotifications: () => axios.get(`${API_BASE}/notifications`),
  addNotification: (data) => axios.post(`${API_BASE}/notifications`, data),
  deleteNotification: (id) => axios.delete(`${API_BASE}/notifications/${id}`),

  // Leave Management
  getAllLeaves: () => axios.get(`${API_BASE}/leave/all`),
  getMyLeaves: (userId) => axios.get(`${API_BASE}/leave/user/${userId}`), // For teachers
  applyLeave: (data) => axios.post(`${API_BASE}/leave/apply`, data), // For teachers
  updateLeaveStatus: (id, data) =>
    axios.put(`${API_BASE}/leave/${id}/status`, data),

  // Admin Profile & System
  getMe: () => axios.get(`${API_BASE}/admin/me`),
  updateAdminProfile: (data) => axios.put(`${API_BASE}/admin/profile`, data),
  updateAdminPassword: (data) => axios.put(`${API_BASE}/admin/password`, data),
  getAuditLogs: (params = {}) =>
    axios.get(`${API_BASE}/admin/audit-logs`, { params }),

  // Teacher Profile & Settings
  updateTeacherProfile: (data) => axios.put(`${API_BASE}/teacher/profile`, data),
  updateTeacherPassword: (data) => axios.put(`${API_BASE}/teacher/password`, data),
  requestTeacherPasswordOTP: (data) => axios.post(`${API_BASE}/teacher/request-password-otp`, data),

  // Academic Management
  getAllAssignments: (sectionId) =>
    axios.get(
      `${API_BASE}/academics/manage/assignments${sectionId ? `?sectionId=${sectionId}` : ""}`,
    ),
  createAssignment: (data) =>
    axios.post(`${API_BASE}/academics/manage/assignments`, data),
  updateAssignment: (id, data) =>
    axios.put(`${API_BASE}/academics/manage/assignments/${id}`, data),
  deleteAssignment: (id) =>
    axios.delete(`${API_BASE}/academics/manage/assignments/${id}`),
  getStudentGrades: (studentId) =>
    axios.get(`${API_BASE}/academics/manage/grades/${studentId}`),
  updateStudentGrades: (data) =>
    axios.post(`${API_BASE}/academics/manage/grades`, data),

  // Auth
  registerAdmin: (data) => axios.post(`${API_BASE}/admin/register`, data),
  verifyEmailAdmin: (data) =>
    axios.post(`${API_BASE}/admin/verify-email`, data),
  loginAdmin: (data) => axios.post(`${API_BASE}/admin/login`, data),
  verifyLoginOTP: (data) => axios.post(`${API_BASE}/admin/login-verify`, data),
  forgotPasswordAdmin: (data) =>
    axios.post(`${API_BASE}/admin/forgot-password`, data),
  resetPasswordAdmin: (data) =>
    axios.post(`${API_BASE}/admin/reset-password`, data),
  requestPasswordOTP: (data) =>
    axios.post(`${API_BASE}/admin/request-password-otp`, data),

  // Exams
  getExams: () => axios.get(`${API_BASE}/exams`),
  createExam: (data) => axios.post(`${API_BASE}/exams`, data),
  updateExamStatus: (id, status) =>
    axios.put(`${API_BASE}/exams/${id}/status`, { status }),
  getExamSchedules: (examId) =>
    axios.get(`${API_BASE}/exams/${examId}/schedule`),
  addExamSchedule: (examId, data) =>
    axios.post(`${API_BASE}/exams/${examId}/schedule`, data),
  deleteExamSchedule: (id) => axios.delete(`${API_BASE}/exams/schedule/${id}`),
  generateExamSchedule: (examId, data) =>
    axios.post(`${API_BASE}/exams/${examId}/generate-schedule`, data),

  // Sessions
  getSessions: () => axios.get(`${API_BASE}/admin/sessions`),
  logoutSession: (id) => axios.delete(`${API_BASE}/admin/sessions/${id}`),
  logoutAllDevices: () => axios.post(`${API_BASE}/admin/logout-all`),

  // Subject Progress
  updateSubjectProgress: (subjectId, percentageCompleted) =>
    axios.put(`${API_BASE}/subjects/${subjectId}/progress`, {
      percentageCompleted,
    }),

  // Resources & Inventory
  getResources: () => axios.get(`${API_BASE}/resources`),
  addResource: (data) => axios.post(`${API_BASE}/resources`, data),
  deleteResource: (id) => axios.delete(`${API_BASE}/resources/${id}`),
  getBookings: () => axios.get(`${API_BASE}/bookings`),
  addBooking: (data) => axios.post(`${API_BASE}/bookings`, data),
  updateBookingStatus: (id, status) =>
    axios.put(`${API_BASE}/bookings/${id}/status`, { status }),

  // Discipline
  getDisciplineLogs: (params) =>
    axios.get(`${API_BASE}/discipline-logs`, { params }),
  addDisciplineLog: (data) => axios.post(`${API_BASE}/discipline-logs`, data),

  // Report Exports (returns blob for file download)
  exportAttendanceReport: (params = {}) =>
    axios.get(`${API_BASE}/export/attendance`, {
      params,
      responseType: "blob",
    }),
  exportGradeReport: (params = {}) =>
    axios.get(`${API_BASE}/export/grades`, { params, responseType: "blob" }),

  // Bulk Import
  bulkImportStudents: (file) => {
    const formData = new FormData();
    formData.append("file", file);
    return axios.post(`${API_BASE}/bulk-import/students`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
      timeout: 120000, // 2 min timeout for large files
    });
  },
  bulkImportTeachers: (file) => {
    const formData = new FormData();
    formData.append("file", file);
    return axios.post(`${API_BASE}/bulk-import/teachers`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
      timeout: 120000,
    });
  },
  downloadStudentTemplate: () =>
    axios.get(`${API_BASE}/bulk-import/template/students`, {
      responseType: "blob",
    }),
  downloadTeacherTemplate: () =>
    axios.get(`${API_BASE}/bulk-import/template/teachers`, {
      responseType: "blob",
    }),

  // System Configuration
  getSystemConfig: () => axios.get(`${API_BASE}/config/system`),
  updateSystemConfig: (values) =>
    axios.put(`${API_BASE}/config/system`, { values }),
  resetSystemConfig: (category) =>
    axios.post(`${API_BASE}/config/system/reset`, { category }),

  // Public
  getPublicInfo: () => axios.get(`${API_BASE}/public/info`),
};

export default api;
